import { authApi } from './api';

/**
 * Dashboard -> browser extension sign-in handoff.
 *
 * After the user signs in here, the dashboard asks the API for a separate
 * token pair and passes it to the extension over Chrome's external messaging
 * channel, so the user never signs in twice. The extension only accepts these
 * messages from this dashboard's origin (see extension/src/background/external.js).
 *
 * The id is fixed by the `key` in extension/manifest.json.
 */
export const EXTENSION_ID = process.env.REACT_APP_EXTENSION_ID || 'jgbkaaebilnjgkbnilpjoapjopaeifmd';

export const EXTENSION_STATUS_EVENT = 'pathfindr:extension-status';

export interface ExtensionStatus {
  installed: boolean;
  version?: string;
  authenticated: boolean;
  userId: string | null;
  email: string | null;
  /** The user signed out from the extension itself; only an explicit connect may sign it back in. */
  optedOut: boolean;
}

const NOT_INSTALLED: ExtensionStatus = {
  installed: false,
  authenticated: false,
  userId: null,
  email: null,
  optedOut: false,
};

type Reply =
  | { ok: true; data: ExtensionStatus & { signedIn?: boolean; signedOut?: boolean; armed?: boolean; reason?: string } }
  | { ok: false; error: string };

interface ChromeRuntime {
  sendMessage: (id: string, message: unknown, callback: (reply?: Reply) => void) => void;
  lastError?: { message?: string };
}

/**
 * Chrome exposes `chrome.runtime.sendMessage` to a page only when an installed
 * extension lists the page's origin under `externally_connectable`. Anywhere
 * else (other browsers, extension not installed) this resolves to null.
 */
function send(message: Record<string, unknown>, timeoutMs = 3000): Promise<Reply | null> {
  return new Promise((resolve) => {
    const runtime = (window as unknown as { chrome?: { runtime?: ChromeRuntime } }).chrome?.runtime;
    if (!runtime?.sendMessage) {
      resolve(null);
      return;
    }
    let settled = false;
    const finish = (value: Reply | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve(value);
    };
    const timer = window.setTimeout(() => finish(null), timeoutMs);
    try {
      runtime.sendMessage(EXTENSION_ID, message, (reply) => {
        // Reading lastError marks it handled; it is set when the id is not installed.
        if (runtime.lastError || !reply) finish(null);
        else finish(reply);
      });
    } catch {
      finish(null);
    }
  });
}

function publish(status: ExtensionStatus): ExtensionStatus {
  window.dispatchEvent(new CustomEvent<ExtensionStatus>(EXTENSION_STATUS_EVENT, { detail: status }));
  return status;
}

export async function getExtensionStatus(): Promise<ExtensionStatus> {
  const reply = await send({ type: 'PATHFINDR_EXT_STATUS' });
  return reply?.ok ? reply.data : NOT_INSTALLED;
}

export class ExtensionConnectError extends Error {}

let inFlight: Promise<ExtensionStatus> | null = null;

/**
 * Make the extension signed in as `userId`. Does nothing when it already is,
 * when it is not installed, or (unless `explicit`) when the user deliberately
 * signed out of the extension. Concurrent calls share one attempt so only one
 * token pair is minted.
 */
export function connectExtension(userId: string, explicit = false): Promise<ExtensionStatus> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    const status = await getExtensionStatus();
    if (!status.installed) return publish(status);
    if (status.authenticated && status.userId === userId) return publish(status);
    if (status.optedOut && !explicit) return publish(status);

    const { tokens } = await authApi.mintExtensionTokens();
    const reply = await send({ type: 'PATHFINDR_EXT_SIGN_IN', tokens, explicit }, 15000);
    if (!reply?.ok || !reply.data.signedIn) {
      // The pair was never stored by the extension; do not leave it valid.
      authApi.logout(tokens.refresh).catch(() => undefined);
      if (reply?.ok) return publish(reply.data);
      throw new ExtensionConnectError(reply?.error || 'The extension did not respond');
    }
    return publish(reply.data);
  })().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** Sign the extension out when it is signed in as `userId`. Best effort. */
export async function disconnectExtension(userId: string | null | undefined): Promise<void> {
  if (!userId) return;
  const reply = await send({ type: 'PATHFINDR_EXT_SIGN_OUT', userId });
  if (reply?.ok) publish(reply.data);
}

export type PrefillMode = 'fill' | 'tailored';

export interface PrefillMeta {
  jobId?: string;
  title?: string;
  company?: string;
}

export interface PrefillResult {
  armed: boolean;
  reason?: string;
}

/** One click raises several events (pointerdown, click); they share one message. */
const PREFILL_DEDUPE_MS = 2000;

let lastArm: { key: string; at: number; result: Promise<PrefillResult> } | null = null;

/**
 * Tell the extension the user is about to open `url`, so it fills the form
 * there as soon as the page loads. Called just before the browser follows the
 * link, so it never throws and nothing waits on it: without the extension the
 * link simply opens as usual.
 */
export function armPrefill(url: string, mode: PrefillMode, meta: PrefillMeta = {}): Promise<PrefillResult> {
  const key = `${mode} ${url}`;
  const now = Date.now();
  if (lastArm && lastArm.key === key && now - lastArm.at < PREFILL_DEDUPE_MS) return lastArm.result;
  const result = (async (): Promise<PrefillResult> => {
    try {
      const reply = await send({
        type: 'PATHFINDR_EXT_PREFILL',
        url,
        mode,
        jobId: meta.jobId,
        title: meta.title,
        company: meta.company,
      });
      if (!reply) return { armed: false, reason: 'not_installed' };
      if (!reply.ok) return { armed: false, reason: reply.error };
      publish(reply.data);
      return { armed: reply.data.armed === true, reason: reply.data.reason };
    } catch {
      return { armed: false };
    }
  })();
  lastArm = { key, at: now, result };
  return result;
}
